/**
 * useWebRTCConnection — WebRTC peer refs and TURN credential cache
 * Phase 4 (PRI-291): extracted from useStreamSession
 *
 * Owns: webrtcRef, dataChannelRef, preConnectWebRTCRef, preConnectDataChannelRef, cachedTurnRef
 * The WebRTCClient lifecycle (create/teardown/offer/answer) stays in the orchestrator since
 * it's interleaved with signalling callbacks, metering, and connection state.
 */

import { useRef } from 'react'
import type { WebRTCClient } from '../../lib/webrtc'

export interface UseWebRTCConnectionReturn {
  /** The active stream WebRTCClient — populated during startStream, cleared on cleanup */
  webrtcRef: React.MutableRefObject<WebRTCClient | null>
  /** The main data channel for prompt-control messages */
  dataChannelRef: React.MutableRefObject<RTCDataChannel | null>
  /** The pre-connect WebRTCClient — populated during preConnect, transferred on Submit */
  preConnectWebRTCRef: React.MutableRefObject<WebRTCClient | null>
  /** The pre-connect data channel — used to send initial_prompt on Submit */
  preConnectDataChannelRef: React.MutableRefObject<RTCDataChannel | null>
  /** Pre-warmed TURN credentials (< 4 min old reused; expired fetch triggers a fresh fetch) */
  cachedTurnRef: React.MutableRefObject<{ servers: RTCIceServer[]; fetchedAt: number } | null>
}

export function useWebRTCConnection(): UseWebRTCConnectionReturn {
  const webrtcRef = useRef<WebRTCClient | null>(null)
  const dataChannelRef = useRef<RTCDataChannel | null>(null)
  const preConnectWebRTCRef = useRef<WebRTCClient | null>(null)
  const preConnectDataChannelRef = useRef<RTCDataChannel | null>(null)
  const cachedTurnRef = useRef<{ servers: RTCIceServer[]; fetchedAt: number } | null>(null)

  return {
    webrtcRef,
    dataChannelRef,
    preConnectWebRTCRef,
    preConnectDataChannelRef,
    cachedTurnRef,
  }
}
